// Detecting an OUTBOUND ACTION in a gated mission step.
//
// Leap 4: when a mission's gated step is an outbound action (a message the Familiar drafted
// to send OUT of your world), the gate renders as an ACTION approval, not a generic "approve
// the draft". The Familiar marks such a step by leading the artifact with a small envelope of
// blockquote meta lines — `> channel:`, `> to:`, `> subject:` — followed by the message body:
//
//   > channel: email
//   > to: jane.doe@northwind.vc
//   > subject: Intro — Diwata × Northwind
//
//   Hi Jane, …
//
// Pure and dependency-free so the parse can be unit-tested. When no envelope is present we
// still treat a step under an outbound protocol as an action (the whole artifact is the body),
// because those protocols exist to produce something you send.

export interface OutboundAction {
  channel: string
  to: string
  subject?: string
  /** The message body — the artifact with the envelope stripped. */
  body: string
}

// Protocols whose gated step exists to produce an outbound message. Used as the fallback
// signal when an artifact carries no explicit envelope.
export const OUTBOUND_PROTOCOLS = new Set([
  'warm-intro/v1',
  'outreach/v1',
])

const META_LINE = /^\s*>\s*(channel|to|subject)\s*:\s*(.+?)\s*$/i

/**
 * Detect an outbound action for a gated step. Returns the parsed action, or null when the
 * step is not an outbound one (so the caller falls back to the generic draft gate).
 *
 * - An explicit `> channel:` / `> to:` envelope is the strong signal: parsed precisely.
 * - Otherwise, a step under an OUTBOUND protocol is still an action, with a sensible default
 *   channel and `to` taken from the mission target.
 */
export function detectOutbound(opts: {
  protocol: string
  artifact: string
  target?: string
}): OutboundAction | null {
  const { protocol, artifact, target } = opts
  const lines = artifact.split('\n')
  const meta: Record<string, string> = {}
  let i = 0
  // Consume leading blank lines, then a contiguous run of `> key: value` meta lines.
  while (i < lines.length && lines[i].trim() === '') i++
  while (i < lines.length) {
    const m = META_LINE.exec(lines[i])
    if (!m) break
    meta[m[1].toLowerCase()] = m[2]
    i++
  }
  const hasEnvelope = 'channel' in meta || 'to' in meta

  if (hasEnvelope) {
    // Strip the envelope + the blank line that follows it from the body.
    while (i < lines.length && lines[i].trim() === '') i++
    const body = lines.slice(i).join('\n').trim()
    return {
      channel: meta.channel ?? 'message',
      to: meta.to ?? (target ?? '—'),
      subject: meta.subject,
      body: body || artifact.trim(),
    }
  }

  if (OUTBOUND_PROTOCOLS.has(protocol)) {
    return {
      channel: protocol === 'warm-intro/v1' ? 'intro' : 'message',
      to: target?.trim() || '—',
      body: artifact.trim(),
    }
  }

  return null
}
